/**
 * Google Analytics 4 (gtag.js), loaded only on geosportle.app in production builds and only when
 * the visitor's consent allows it (lib/consent.ts). Ads features and Google signals are off and
 * cookies last 13 months. Page views are sent by the app after each route's title is set, so the
 * stream's "page changes based on browser history events" setting is switched off in GA.
 */
import { GA_MEASUREMENT_ID, GAME_DOMAIN } from '@geosport/shared';
import { addAnalyticsSink, type Props } from './analytics';
import { analyticsAllowed, useConsent } from './consent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let loaded = false;
let active = false;

/** Production site only: previews, localhost and tests never send data. */
const available = (): boolean => import.meta.env.PROD && window.location.hostname === GAME_DOMAIN;

function load(): void {
  if (loaded) return;
  loaded = true;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // gtag.js reads `arguments` objects from the data layer; rest-parameter arrays are ignored.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
  });
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_expires: 13 * 30 * 24 * 60 * 60,
    content_group: 'app',
  });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

function enable(): void {
  if (!available()) return;
  const first = !loaded;
  load();
  if (!first) window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
  active = true;
  gaPageView();
}

/** Stops collection and removes the GA cookies (_ga, _ga_<stream>). */
export function disableGa(): void {
  active = false;
  window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0]!.trim())
    .filter((n) => n === '_ga' || n.startsWith('_ga_'));
  for (const name of names) {
    for (const domain of ['', `; domain=${window.location.hostname}`, `; domain=.${GAME_DOMAIN}`]) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
    }
  }
}

/** A page view for the current URL, with the title the app just set. */
export function gaPageView(): void {
  if (!active) return;
  window.gtag?.('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
  });
}

/** "puzzleNo" → "puzzle_no"; nulls dropped; booleans as text (GA4 parameter conventions). */
export function toGaParams(props?: Props): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(props ?? {})) {
    if (value === null) continue;
    out[key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)] =
      typeof value === 'boolean' ? String(value) : value;
  }
  return out;
}

let started = false;

/** Called once after first paint: forwards app events to GA and follows the consent choice. */
export function initGa(): void {
  if (started) return;
  started = true;
  addAnalyticsSink((event, props) => {
    if (active) window.gtag?.('event', event, toGaParams(props));
  });
  if (analyticsAllowed(useConsent.getState().choice)) enable();
  useConsent.subscribe((state, prev) => {
    if (state.choice === prev.choice) return;
    if (analyticsAllowed(state.choice)) enable();
    else disableGa();
  });
}
